// (c) 2026 Owl Consultations. All rights reserved.
// G-UI-1 / ASP-68 (D-UI-1-universal-list-ux.md, signed): ONE shared header-filter layer for
// every list surface. Funnel per column header -> popover with the DISTINCT VALUES OF THAT
// COLUMN FROM THE CURRENTLY LOADED ROWS (Sasha ruling §6: options derive only from rows the
// view already renders — zero new data exposure by construction), checkbox multi-select,
// search-within for long sets, Clear. Active = filled funnel + chip above the table. Header
// filters AND-compose with whatever filtering the page already did (mf deep-links, tabs,
// search) — they re-slice, never replace.
import { useMemo, useRef, useState, useEffect } from 'react'
import { createPortal } from 'react-dom'
import { Filter, X } from 'lucide-react'

export type ColumnFilters = Record<string, Set<string>>

const BLANK = '(blank)'
// Date-range funnel bounds are stored inside the Set<string> (type unchanged): "≥YYYY-MM-DD" / "≤YYYY-MM-DD".
const GE = '\u2265'
const LE = '\u2264'
// D-UI-2 (every column filters): a free-text "contains" funnel, for the columns that are never
// listed as values (a name, an MRN, a phone number, free-text directions). Same carrier as the
// range bounds, so the filter state keeps ONE type: a single token, the prefix then the term.
export const CONTAINS = '\u220B'

/** The text a contains-funnel is holding, or '' when the column has none. */
export const containsTerm = (set: Set<string> | undefined): string =>
  (set ? [...set].find(t => t.startsWith(CONTAINS))?.slice(1) : undefined) ?? ''

const NUMERIC = /^-?\d*\.?\d+$/

/** A cell as a number, for range funnels over money, counts and percentages; null when it is not one. */
export const toNumberKey = (v: unknown): number | null => {
  if (v === null || v === undefined || v === '' || v === '\u2014') return null
  if (typeof v === 'number') return Number.isFinite(v) ? v : null
  const s = String(v).replace(/[$,%\s]/g, '')
  if (!NUMERIC.test(s)) return null
  const n = Number(s)
  return Number.isFinite(n) ? n : null
}
const isNumericBound = (s: string | undefined) => s === undefined || NUMERIC.test(s)

/** How a funnel's state reads in a chip or a tooltip: `contains "smi"`, `\u2265 10`, or the value itself. */
export const describeToken = (t: string): string =>
  t.startsWith(CONTAINS) ? `contains "${t.slice(1)}"`
    : t.startsWith(GE) ? `${GE} ${t.slice(1)}`
    : t.startsWith(LE) ? `${LE} ${t.slice(1)}`
    : t

export type FunnelServerOp = 'in' | 'gte' | 'lte' | 'contains'

/**
 * One funnel's state as SERVER predicates, against the operators the server says the column
 * admits. `blocked` means the state cannot be expressed and must be REPORTED by the page, never
 * dropped. Every server-paged list translates through this one function; the five copies it
 * replaces had each learned ranges separately and none had learned contains.
 */
export function funnelToServerOps(
  set: Set<string> | undefined, ops: readonly string[] | undefined,
): { preds: { op: FunnelServerOp; values: string[] }[]; blocked: boolean } {
  const preds: { op: FunnelServerOp; values: string[] }[] = []
  if (!set || set.size === 0) return { preds, blocked: false }
  const can = (op: string) => !ops || ops.includes(op)
  const arr = Array.from(set)
  if (arr.some(t => t.startsWith(CONTAINS))) {
    const term = containsTerm(set).trim()
    if (!term) return { preds, blocked: false }
    if (!can('contains')) return { preds, blocked: true }
    return { preds: [{ op: 'contains', values: [term] }], blocked: false }
  }
  const from = arr.find(t => t.startsWith(GE))?.slice(1)
  const to = arr.find(t => t.startsWith(LE))?.slice(1)
  if (from || to) {
    if (from && can('gte')) preds.push({ op: 'gte', values: [from] })
    if (to && can('lte')) preds.push({ op: 'lte', values: [to] })
    return { preds, blocked: (!!from && !can('gte')) || (!!to && !can('lte')) }
  }
  if (!can('in')) return { preds, blocked: true }
  return { preds: [{ op: 'in', values: arr }], blocked: false }
}

/** Normalize any cell value to a YYYY-MM-DD key for date-range comparison, or null. */
export const toDateKey = (v: unknown): string | null => {
  if (v === null || v === undefined || v === '' || v === '\u2014') return null
  const d = new Date(v as any)
  return isNaN(d.getTime()) ? null : d.toISOString().slice(0, 10)
}

/** Normalize a cell to its filter token. */
export const filterToken = (v: unknown): string => {
  if (v === null || v === undefined) return BLANK
  const s = String(v).trim()
  return s === '' ? BLANK : s
}

/**
 * D-WB-TASKTYPE-FORMAT Part 2 (ClickUp 86cb27pa9) — split a MULTI-SELECT cell into its individual
 * values so the funnel offers "New Order" and "Copay Collection", not the blob
 * `["New Order","Copay Collection"]` (and its comma-joined twin) as two separate options.
 *
 * Two encodings exist in the data on purpose-by-accident: the WorkBench UI writes a JSON array
 * (canonical), while backfill migrations 315/316 wrote comma-joined text. Migration 322 normalizes
 * the stored data; this makes the funnel readable EITHER WAY, which is why it is worth shipping
 * before the migration rather than after.
 *
 * COMMA SPLITTING IS OPT-IN, and that is the important part. Plenty of legitimate single values
 * contain a comma (a name, a note, "Refill, Welcome Call" as one configured option) — mig 322 hit
 * exactly this and had to add a sentinel. So a bare comma-joined string is only split for columns
 * the caller states are multi-select (`selection_mode = 'multi'` on the WorkBench field def).
 * A JSON array is unambiguous and is always split.
 */
export function decomposeMulti(v: unknown, isMulti: boolean): string[] {
  const raw = filterToken(v)
  if (raw === BLANK) return [BLANK]
  if (raw.startsWith('[')) {
    try {
      const arr = JSON.parse(raw)
      if (Array.isArray(arr)) {
        const vals = arr.map(x => String(x).trim()).filter(Boolean)
        return vals.length > 0 ? vals : [BLANK]
      }
    } catch { /* not JSON after all — fall through and treat as a plain value */ }
  }
  if (isMulti && raw.includes(',')) {
    const vals = raw.split(',').map(s => s.trim()).filter(Boolean)
    return vals.length > 0 ? vals : [BLANK]
  }
  return [raw]
}

/** Apply active column filters to rows via each column's raw-value accessor. */
// D-VIEWS: stable signature of the column-filter SELECTIONS (not the filtered result), for keying
// pagination resets so auto-refresh doesn't snap the pager to page 1.
export function columnFilterSig(cf: ColumnFilters): string {
  return Object.keys(cf).sort().map(k => `${k}:${Array.from(cf[k]).sort().join(',')}`).join('|')
}

export function applyColumnFilters<T>(
  rows: T[],
  filters: ColumnFilters,
  getValue: (row: T, key: string) => unknown,
  /**
   * Column keys whose cells hold MULTIPLE values (WorkBench `selection_mode = 'multi'`).
   * For these, a row matches when ANY of its values is ticked — picking "Copay Collection" must
   * return the rows that also carry "New Order", or the filter contradicts what the cell shows.
   * Omitted = previous behaviour exactly (whole-cell equality).
   */
  multiKeys?: Set<string>,
): T[] {
  const active = Object.entries(filters).filter(([, set]) => set.size > 0)
  if (active.length === 0) return rows
  return rows.filter(row => active.every(([key, set]) => {
    const arr = [...set]
    if (arr.some(t => t.startsWith(CONTAINS))) {   // free-text mode
      const needle = containsTerm(set).trim().toLowerCase()
      if (!needle) return true
      const cell = getValue(row, key)
      return cell !== null && cell !== undefined && String(cell).toLowerCase().includes(needle)
    }
    const from = arr.find(t => t.startsWith(GE))?.slice(1)
    const to = arr.find(t => t.startsWith(LE))?.slice(1)
    if ((from || to) && isNumericBound(from) && isNumericBound(to)) {   // number-range mode
      // Bounds that are plain numbers compare as numbers. They used to fall into the date branch
      // below, where "5" parsed as a day in 2001 and every row was dropped.
      const n = toNumberKey(getValue(row, key))
      if (n === null) return false
      if (from && n < Number(from)) return false
      if (to && n > Number(to)) return false
      return true
    }
    if (from || to) {                         // date-range mode
      const d = toDateKey(getValue(row, key))
      if (!d) return false
      if (from && d < from) return false
      if (to && d > to) return false
      return true
    }
    const raw = getValue(row, key)
    // JSON-array cells decompose even when the caller did not flag the column, so a stale
    // multi-select value still filters correctly; comma splitting stays opt-in (see decomposeMulti).
    const isMulti = multiKeys?.has(key) ?? false
    const tokens = isMulti || String(filterToken(raw)).startsWith('[')
      ? decomposeMulti(raw, isMulti)
      : [filterToken(raw)]
    return tokens.some(t => set.has(t))
  }))
}

/** Funnel button + popover for one column. Values come from the rows PRE-filter for this
 *  column (so unticking doesn't erase its own options) — pass them in. */
/** D-RXFILTER-1: what a server-backed option source returns. */
export interface ServerFilterOptions {
  options: { value: string; label: string }[]
  /** configured | distinct | entity | range | not_enumerable — drives what the popover renders. */
  source: string
  truncated: boolean
  notice: string | null
}

/** Options drawn at once from a browser-held list; the search box reaches the rest. */
const LOCAL_OPTION_CAP = 300
/** How long a typed bound rests before it is applied. */
const RANGE_COMMIT_MS = 700

export function ColumnFilterButton({
  label, values, getValues, selected, onChange, isDate, isNumeric, isText, onOpen, fetchOptions, labelFor, onLabels,
}: {
  label: string
  /** Eager list (legacy call sites). Prefer getValues so nothing is computed until the menu opens. */
  values?: string[]
  /**
   * D-PERF-5: lazy option source. Computing distinct values scans every loaded row, and doing that
   * for every column on every render was the keystroke lag in the Prescriptions search (each keypress
   * re-rendered the page and re-scanned ~20 columns x thousands of rows). Called ONLY while the menu
   * is open, so a keystroke now costs nothing here.
   */
  getValues?: () => string[]
  selected: Set<string>
  onChange: (next: Set<string>) => void
  /** Render a from/to DATE-RANGE picker instead of the distinct-value checkbox list. */
  isDate?: boolean
  /**
   * Render a min/max NUMBER-RANGE picker. Same ≥/≤ tokens as the date range, so the page's
   * translation to server predicates is identical and there is one bound-encoding, not two.
   *
   * D-RXFILTER-4: numeric columns already accepted gte/lte at the API (copay, carrier cost,
   * rx_total on prescribers) but had no control here, so the capability existed and could not be
   * reached. A funnel that silently offers nothing is the same defect class as one that silently
   * offers too little.
   */
  isNumeric?: boolean
  /**
   * Render a free-text CONTAINS box instead of a value list (D-UI-2). For the columns whose values
   * are never listed: a name, an MRN, a phone number, free text. The funnel is still there and
   * still filters; it just does not enumerate. A server source that answers `not_enumerable`
   * switches to this mode by itself, so a page that passed nothing still gets a working control.
   */
  isText?: boolean
  /** RX-PERF-13: fired when the menu opens — lets a page lazy-load the values this
   *  column's options derive from (custom/drug-ref columns resolve on demand). */
  onOpen?: () => void
  /**
   * D-RXFILTER-1: SERVER-BACKED option source. When present it REPLACES values/getValues.
   *
   * This is the whole point of the change. Deriving options from loaded rows meant a funnel showed
   * the four pharmacy queues that happened to appear in the newest 5,000 rows and hid the rest —
   * a sample presented as a filter. The server answers from the column's own authority instead:
   * the configured choice list, a TOP-capped distinct, or a typeahead over the entity.
   *
   * Called when the menu opens and again (debounced) as the user types.
   */
  fetchOptions?: (term: string) => Promise<ServerFilterOptions>
  /** Render a stored value as a human label. Entity filters store IDs, so without this the
   *  checkbox list and the chips would show raw GUIDs. */
  labelFor?: (value: string) => string | undefined
  /** Report value->label pairs back so the page can label chips for values it has never seen. */
  onLabels?: (pairs: { value: string; label: string }[]) => void
}) {
  const [open, setOpen] = useState(false)
  const [q, setQ] = useState('')
  const ref = useRef<HTMLSpanElement | null>(null)
  const btnRef = useRef<HTMLButtonElement | null>(null)
  const popRef = useRef<HTMLDivElement | null>(null)
  const [pos, setPos] = useState<{ top: number; left: number; maxHeight: number } | null>(null)
  const active = selected.size > 0
  // D-VIEWS-SRV-1 WS3 follow-up (ASP 2026-09-28): a numeric column whose page did not pass
  // isNumeric — the custom-view tab only knew dates — fell into the list branch, and the server's
  // klass:"range" answer (options: []) rendered as "No values". The server's own classification
  // is authoritative: a range answer is a Min/Max picker, whatever the page assumed.
  // Date-range mode: read/write the ≥from / ≤to bounds carried in the Set.
  const curFrom = [...selected].find(t => t.startsWith(GE))?.slice(1) ?? ''
  const curTo = [...selected].find(t => t.startsWith(LE))?.slice(1) ?? ''
  const setRange = (from: string, to: string) => {
    const s = new Set<string>()
    if (from) s.add(GE + from)
    if (to) s.add(LE + to)
    onChange(s)
  }
  // Bounds are typed, and on a server-paged list every committed change is a tenant-DB query.
  // Typing "2026" used to send four. The inputs edit a draft; the draft commits after a pause,
  // on Enter, or when the menu closes.
  const [draftFrom, setDraftFrom] = useState(curFrom)
  const [draftTo, setDraftTo] = useState(curTo)
  const draftRef = useRef({ from: curFrom, to: curTo, curFrom, curTo })
  draftRef.current = { from: draftFrom, to: draftTo, curFrom, curTo }
  const setRangeRef = useRef(setRange)
  setRangeRef.current = setRange
  const commitRange = () => {
    const d = draftRef.current
    if (d.from !== d.curFrom || d.to !== d.curTo) setRangeRef.current(d.from, d.to)
  }
  useEffect(() => {
    if (open) { setDraftFrom(curFrom); setDraftTo(curTo) }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, curFrom, curTo])
  useEffect(() => {
    if (!open) return
    if (draftFrom === curFrom && draftTo === curTo) return
    const t = setTimeout(commitRange, RANGE_COMMIT_MS)
    return () => clearTimeout(t)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, draftFrom, draftTo])
  // Closing the menu with a bound still in the draft applies it rather than losing it.
  const wasOpen = useRef(false)
  useEffect(() => {
    if (wasOpen.current && !open) commitRange()
    wasOpen.current = open
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open])
  const onRangeKey = (e: { key: string }) => { if (e.key === 'Enter') commitRange() }

  // Close on outside click. The popover is portalled to <body>, so it is NOT inside `ref`;
  // check both the header anchor and the popover so a click inside the menu doesn't close it.
  useEffect(() => {
    if (!open) return
    const onDoc = (e: MouseEvent) => {
      const t = e.target as Node
      if (ref.current?.contains(t) || popRef.current?.contains(t)) return
      setOpen(false)
    }
    document.addEventListener('mousedown', onDoc)
    return () => document.removeEventListener('mousedown', onDoc)
  }, [open])

  // ASP-121: the menu was position:absolute inside the header cell, so a scrollable/overflow
  // ancestor clipped it. Portal it to <body> and position it fixed off the funnel button's rect,
  // clamped to the viewport, so it can never be cut off.
  useEffect(() => {
    if (!open) { setPos(null); return }
    const place = () => {
      const r = btnRef.current?.getBoundingClientRect()
      if (!r) return
      const W = 280
      let left = r.left
      if (left + W > window.innerWidth - 8) left = Math.max(8, window.innerWidth - W - 8)
      const top = r.bottom + 4
      setPos({ top, left, maxHeight: Math.max(160, window.innerHeight - top - 12) })
    }
    place()
    window.addEventListener('scroll', place, true)
    window.addEventListener('resize', place)
    return () => { window.removeEventListener('scroll', place, true); window.removeEventListener('resize', place) }
  }, [open])

  // ---- Option resolution -------------------------------------------------------------------
  // Two sources, one contract (LSP): whichever is in play, `shown` ends up a {value,label} list.
  //  - local   legacy/client columns: distinct values of the rows already loaded
  //  - server  fetchOptions: the column's own authority, searched as the user types
  const [srv, setSrv] = useState<ServerFilterOptions | null>(null)
  const numericMode = !!isNumeric || srv?.source === 'range'
  const [srvLoading, setSrvLoading] = useState(false)
  const [srvError, setSrvError] = useState<string | null>(null)

  // Resolve local options only while open; closed headers cost nothing.
  const resolvedValues = useMemo(
    () => (open && !fetchOptions ? (getValues ? getValues() : (values ?? [])) : []),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [open, values, getValues, fetchOptions])

  // Server source: fetch on open, then on each (debounced) keystroke. The request is superseded
  // rather than cancelled-and-lost — a stale response must never overwrite a newer one.
  const fetchRef = useRef(fetchOptions)
  fetchRef.current = fetchOptions
  const onLabelsRef = useRef(onLabels)
  onLabelsRef.current = onLabels

  // How this column answers a keystroke.
  //  loaded       — the open-time fetch has returned
  //  serverSearch — subsequent typing needs the SERVER, or can be answered locally
  //
  // Most columns do NOT need the server to search. A configured or distinct list arrives complete
  // when the menu opens (capped at 50), so filtering it is a substring test over an array already
  // in memory — typing "testaqwe" into the state funnel should cost ZERO requests, not eight. Only
  // two cases genuinely need a round trip: an entity typeahead (the list is a query over thousands
  // of patients), and a distinct list that hit its cap, where the value being sought may lie
  // beyond what was returned.
  const modeRef = useRef({ loaded: false, serverSearch: true })
  const SEARCH_DEBOUNCE_MS = 1000

  useEffect(() => {
    if (!open || !fetchRef.current) return
    const mode = modeRef.current
    // Answerable from what we already hold — no request, no debounce, no flicker.
    if (mode.loaded && !mode.serverSearch) return

    let dead = false
    const t = setTimeout(() => {
      setSrvLoading(true); setSrvError(null)
      fetchRef.current!(q.trim())
        .then(r => {
          if (dead) return
          modeRef.current = { loaded: true, serverSearch: r.source === 'entity' || r.truncated }
          setSrv(r); onLabelsRef.current?.(r.options)
        })
        .catch(e => { if (!dead) setSrvError(e?.body?.message || e?.message || 'Could not load values.') })
        .finally(() => { if (!dead) setSrvLoading(false) })
      // The open-time fetch is immediate; only typing is debounced. A full second is deliberate:
      // each of these is a tenant-DB query, and per-keystroke round trips are the load-multiplier
      // shape that took ASP down on 2026-08-06.
    }, mode.loaded ? SEARCH_DEBOUNCE_MS : 0)
    return () => { dead = true; clearTimeout(t) }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, q])

  useEffect(() => {
    if (!open) { setSrv(null); setSrvError(null); setQ(''); modeRef.current = { loaded: false, serverSearch: true } }
  }, [open])


  const shown = useMemo(() => {
    if (fetchOptions) {
      const all = srv?.options ?? []
      // Local narrowing for the columns that returned a complete list (see modeRef above).
      const needle = q.trim().toLowerCase()
      const opts = (!modeRef.current.serverSearch && needle)
        ? all.filter(o => o.label.toLowerCase().includes(needle) || o.value.toLowerCase().includes(needle))
        : all
      // A ticked value that the current search does not return must STILL be listed, or there is
      // no way to untick it — the classic server-typeahead trap. Selected-but-absent goes first.
      const present = new Set(opts.map(o => o.value))
      const orphans = [...selected]
        .filter(v => !present.has(v))
        .map(v => ({ value: v, label: labelFor?.(v) ?? v }))
      return [...orphans, ...opts]
    }
    const needle = q.trim().toLowerCase()
    // A local list may hold codes the page shows under another name; search and show the name.
    const named = resolvedValues.map(v => ({ value: v, label: labelFor?.(v) ?? v }))
    const local = needle
      ? named.filter(o => o.label.toLowerCase().includes(needle) || o.value.toLowerCase().includes(needle))
      : named
    // A tick list is for picking, not for scrolling five thousand names. Past the cap the list
    // says so and the search box reaches the rest.
    return local.length > LOCAL_OPTION_CAP ? local.slice(0, LOCAL_OPTION_CAP) : local
  }, [fetchOptions, srv, selected, labelFor, resolvedValues, q])
  const localOverflow = !fetchOptions && resolvedValues.length > LOCAL_OPTION_CAP && shown.length >= LOCAL_OPTION_CAP

  const notEnumerable = !!fetchOptions && srv?.source === 'not_enumerable'
  const textMode = !numericMode && !isDate && (!!isText || notEnumerable)
  // The contains box commits on Enter or Apply, never per keystroke: on a server-paged list every
  // commit is a tenant-DB query.
  const curTerm = containsTerm(selected)
  const [draft, setDraft] = useState(curTerm)
  useEffect(() => { if (open) setDraft(curTerm) }, [open, curTerm])
  const commitTerm = (t: string) => {
    const v = t.trim()
    onChange(v ? new Set([CONTAINS + v]) : new Set())
  }
  const activeTitle = [...selected].some(t => t.startsWith(GE) || t.startsWith(LE) || t.startsWith(CONTAINS))
    ? `${label}: ${[...selected].map(describeToken).join(', ')}`
    : `${label}: ${selected.size} selected`

  return (
    <span ref={ref} style={{ position: 'relative', display: 'inline-flex' }} onClick={e => e.stopPropagation()}>
      <button ref={btnRef} type="button" aria-label={`Filter ${label}`} aria-expanded={open}
        title={active ? activeTitle : `Filter ${label}`}
        onClick={() => { if (!open) onOpen?.(); setOpen(v => !v) }}
        style={{ border: 'none', background: 'none', cursor: 'pointer', padding: '0 2px', lineHeight: 0, color: active ? 'var(--color-accent, #1a56db)' : 'var(--color-text-mute, #9ca3af)' }}>
        <Filter size={12} fill={active ? 'currentColor' : 'none'} />
      </button>
      {open && pos && createPortal(
        <div ref={popRef} role="dialog" aria-label={`Filter ${label}`} onClick={e => e.stopPropagation()}
          style={{ position: 'fixed', top: pos.top, left: pos.left, zIndex: 1000, minWidth: 200, maxWidth: 280, maxHeight: pos.maxHeight, overflowY: 'auto', background: 'var(--color-surface, #fff)', border: '1px solid var(--color-border, #DED8CC)', borderRadius: 8, boxShadow: '0 8px 24px rgba(0,0,0,0.14)', padding: 8 }}>
          {numericMode ? (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8, minWidth: 200 }}>
              <label style={{ fontSize: 11, color: 'var(--color-text-mute)', display: 'flex', flexDirection: 'column', gap: 3 }}>Min
                <input type="number" step="any" value={draftFrom} onChange={e => setDraftFrom(e.target.value)} onKeyDown={onRangeKey}
                  style={{ fontSize: 12, padding: '5px 7px', borderRadius: 6, border: '1px solid var(--color-border, #DED8CC)' }} />
              </label>
              <label style={{ fontSize: 11, color: 'var(--color-text-mute)', display: 'flex', flexDirection: 'column', gap: 3 }}>Max
                <input type="number" step="any" value={draftTo} onChange={e => setDraftTo(e.target.value)} onKeyDown={onRangeKey}
                  style={{ fontSize: 12, padding: '5px 7px', borderRadius: 6, border: '1px solid var(--color-border, #DED8CC)' }} />
              </label>
            </div>
          ) : isDate ? (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8, minWidth: 200 }}>
              <label style={{ fontSize: 11, color: 'var(--color-text-mute)', display: 'flex', flexDirection: 'column', gap: 3 }}>From
                <input type="date" value={draftFrom} onChange={e => setDraftFrom(e.target.value)} onKeyDown={onRangeKey}
                  style={{ fontSize: 12, padding: '5px 7px', borderRadius: 6, border: '1px solid var(--color-border, #DED8CC)' }} />
              </label>
              <label style={{ fontSize: 11, color: 'var(--color-text-mute)', display: 'flex', flexDirection: 'column', gap: 3 }}>To
                <input type="date" value={draftTo} onChange={e => setDraftTo(e.target.value)} onKeyDown={onRangeKey}
                  style={{ fontSize: 12, padding: '5px 7px', borderRadius: 6, border: '1px solid var(--color-border, #DED8CC)' }} />
              </label>
            </div>
          ) : textMode ? (
            <form onSubmit={e => { e.preventDefault(); commitTerm(draft) }}
              style={{ display: 'flex', flexDirection: 'column', gap: 8, minWidth: 200 }}>
              <label style={{ fontSize: 11, color: 'var(--color-text-mute)', display: 'flex', flexDirection: 'column', gap: 3 }}>{label} contains
                <input type="text" autoFocus value={draft} onChange={e => setDraft(e.target.value)}
                  placeholder="Type part of the value"
                  style={{ fontSize: 12, padding: '5px 7px', borderRadius: 6, border: '1px solid var(--color-border, #DED8CC)' }} />
              </label>
              {srv?.notice && (
                <div style={{ fontSize: 11.5, color: 'var(--color-text-mute)' }}>{srv.notice}</div>
              )}
              <button type="submit" disabled={draft.trim() === curTerm}
                style={{ border: '1px solid var(--color-border, #DED8CC)', background: 'var(--color-accent, #1a56db)', color: '#fff', borderRadius: 6, padding: '4px 8px', cursor: draft.trim() === curTerm ? 'default' : 'pointer', fontSize: 12, opacity: draft.trim() === curTerm ? 0.5 : 1 }}>
                Apply
              </button>
            </form>
          ) : (<>
          {/* The search box is ALWAYS shown for a server source: the option list is a query, not a
              dump, so typing is how you reach values beyond the cap. Local sources keep the old
              rule (only worth showing past 8 values). */}
          {(fetchOptions || resolvedValues.length > 8) && !notEnumerable && (
            <input value={q} onChange={e => setQ(e.target.value)} placeholder={`Search ${label.toLowerCase()}…`}
              style={{ width: '100%', boxSizing: 'border-box', fontSize: 12, padding: '5px 7px', borderRadius: 6, border: '1px solid var(--color-border, #DED8CC)', marginBottom: 6 }} />
          )}
          {/* Refusals and caps are STATED, never silent. A funnel that cannot list values says so,
              and says why — the honest-degradation rule this change exists to enforce. */}
          {srvError && (
            <div style={{ fontSize: 11.5, color: '#991b1b', background: '#fee2e2', border: '1px solid #f0afaf', borderRadius: 6, padding: '5px 7px', marginBottom: 6 }}>
              {srvError}
            </div>
          )}
          {srv?.notice && !srvError && (
            <div style={{ fontSize: 11.5, color: 'var(--color-text-mute)', padding: '2px 4px 6px' }}>
              {srv.notice}
            </div>
          )}
          {localOverflow && (
            <div style={{ fontSize: 11.5, color: 'var(--color-text-mute)', padding: '2px 4px 6px' }}>
              Showing the first {LOCAL_OPTION_CAP} values. Type to find the rest.
            </div>
          )}
          {!notEnumerable && (
          <div style={{ maxHeight: 240, overflowY: 'auto' }}>
            {srvLoading && shown.length === 0 && (
              <div style={{ fontSize: 12, color: 'var(--color-text-mute)', padding: 6 }}>Searching…</div>
            )}
            {!srvLoading && shown.length === 0 && (
              <div style={{ fontSize: 12, color: 'var(--color-text-mute)', padding: 6 }}>No values</div>
            )}
            {shown.map(o => (
              <label key={o.value} style={{ display: 'flex', alignItems: 'center', gap: 7, fontSize: 12.5, padding: '4px 4px', cursor: 'pointer', borderRadius: 5, fontWeight: 400, textTransform: 'none', letterSpacing: 'normal', color: 'var(--color-text, #1f2937)' }}>
                <input type="checkbox" checked={selected.has(o.value)}
                  onChange={() => {
                    const next = new Set(selected)
                    if (next.has(o.value)) next.delete(o.value); else next.add(o.value)
                    onChange(next)
                  }} />
                <span title={o.label} style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{o.label}</span>
              </label>
            ))}
          </div>
          )}
          </>)}
          {active && (
            <button type="button" onClick={() => onChange(new Set())}
              style={{ marginTop: 6, width: '100%', border: '1px solid var(--color-border, #DED8CC)', background: 'none', borderRadius: 6, padding: '4px 8px', cursor: 'pointer', fontSize: 12 }}>
              {textMode || numericMode || isDate ? 'Clear' : `Clear (${selected.size})`}
            </button>
          )}
        </div>,
        document.body,
      )}
    </span>
  )
}

/** Chip row summarizing active header filters; renders nothing when none are active. */
export function ColumnFilterChips({
  filters, labels, onClear, onClearAll, labelFor,
}: {
  filters: ColumnFilters
  labels: Record<string, string>
  onClear: (key: string) => void
  onClearAll: () => void
  /** D-RXFILTER-1: resolve a stored value to its display label. Entity filters store IDs, so
   *  without this a "Patient" chip would read as a raw GUID. */
  labelFor?: (colKey: string, value: string) => string | undefined
}) {
  const active = Object.entries(filters).filter(([, s]) => s.size > 0)
  if (active.length === 0) return null
  return (
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, alignItems: 'center', margin: '6px 0' }}>
      {active.map(([key, set]) => (
        <span key={key} style={{ display: 'inline-flex', alignItems: 'center', gap: 5, fontSize: 12, border: '1px solid var(--color-border, #DED8CC)', borderRadius: 999, padding: '3px 9px', background: 'var(--color-surface-raised, var(--color-surface, #fff))' }}>
          <Filter size={11} />
          <b>{labels[key] ?? key}</b>: {Array.from(set).slice(0, 3).map(v => labelFor?.(key, v) ?? describeToken(v)).join(', ')}{set.size > 3 ? ` +${set.size - 3}` : ''}
          <button type="button" aria-label={`Clear ${labels[key] ?? key} filter`} onClick={() => onClear(key)}
            style={{ border: 'none', background: 'none', cursor: 'pointer', lineHeight: 0, padding: 0 }}>
            <X size={12} />
          </button>
        </span>
      ))}
      <button type="button" onClick={onClearAll}
        style={{ border: 'none', background: 'none', cursor: 'pointer', fontSize: 12, color: 'var(--color-accent, #1a56db)', textDecoration: 'underline' }}>
        Clear all
      </button>
    </div>
  )
}
